import type { InstantSearchServerState } from 'react-instantsearch';

import { isNonEmptyString } from '@/utils/string';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Algolia InstantSearch uiState is intentionally untyped and highly dynamic.
type UiStateAny = Record<string, any>;
export type QueryParams = Record<string, string | number | string[]>;

/**
 * Checks if a price range string has at least one valid numeric value.
 * Price format is "min:max" - this checks that at least one side has a real number.
 * Returns false for ":", ":Infinity", "-Infinity:", etc.
 */
function hasValidPriceRange(priceString: unknown): boolean {
  if (typeof priceString !== 'string' || !priceString.includes(':')) {
    return false;
  }
  const [min, max] = priceString.split(':');
  const minNum = parseFloat(min);
  const maxNum = parseFloat(max);
  // Check if at least one value is a finite number (not NaN, not Infinity)
  return Number.isFinite(minNum) || Number.isFinite(maxNum);
}

/**
 * Parses a v2 facet value in the format "EnglishID|LocalizedLabel"
 */
function parseV2FacetValue(v2Value: string): { id: string; label: string } {
  const pipeIndex = v2Value.indexOf('|');
  if (pipeIndex === -1) {
    return { id: v2Value, label: v2Value };
  }
  return {
    id: v2Value.substring(0, pipeIndex),
    label: v2Value.substring(pipeIndex + 1),
  };
}

/**
 * Builds a map from localized label to English ID using v2 facet data
 */
function buildLabelToIdMap(
  v2Facet: Record<string, number>,
): Map<string, string> {
  const labelToId = new Map<string, string>();
  Object.keys(v2Facet).forEach((v2Value) => {
    const { id, label } = parseV2FacetValue(v2Value);
    labelToId.set(label, id);
  });
  return labelToId;
}

/**
 * Translates a localized facet value to its English equivalent using v2 facets.
 * Falls back to index-based translation if v2 facets are not available.
 */
function translateToEnglish(
  value: string,
  fieldName: string,
  sourceLocale: string,
  facets: Record<string, Record<string, number>> | undefined,
): string {
  if (!facets) {
    console.warn(
      `[Search Translation] No facets available for translation of "${value}" in field "${fieldName}"`,
    );
    return value;
  }

  // If the value itself is already in v2 format (contains |), find the English equivalent
  // by matching on the ID part
  const pipeIndex = value.indexOf('|');
  if (pipeIndex !== -1) {
    const part1 = value.substring(0, pipeIndex).trim();
    const part2 = value.substring(pipeIndex + 1).trim();
    // For type: format is "EnglishID | LocalizedLabel", ID is before pipe
    // For all others: format is "LocalizedLabel | EnglishID", ID is after pipe
    const valueId = fieldName === 'type' ? part1 : part2;
    const valueIdLower = valueId.toLowerCase();

    // Look in English facets (regular or v2) for a value with the same ID
    const enFacetKeys = [
      `${fieldName}_v2.EN_US`,
      `${fieldName}_v2.EN_GB`,
      `${fieldName}.EN_US`,
      `${fieldName}.EN_GB`,
    ];

    for (const enKey of enFacetKeys) {
      const enFacet = facets[enKey];
      if (!enFacet) continue;

      for (const enValue of Object.keys(enFacet)) {
        const enPipeIndex = enValue.indexOf('|');
        if (enPipeIndex !== -1) {
          const enPart1 = enValue
            .substring(0, enPipeIndex)
            .trim()
            .toLowerCase();
          const enPart2 = enValue
            .substring(enPipeIndex + 1)
            .trim()
            .toLowerCase();
          // Match if either part equals our ID
          if (enPart1 === valueIdLower || enPart2 === valueIdLower) {
            return enValue;
          }
        }
      }
    }

    // No English match found, return original value
    return value;
  }

  // Try to find v2 facet for this field and locale
  const v2Key = `${fieldName}_v2.${sourceLocale}`;
  const alternateV2Key =
    sourceLocale === 'EN_US'
      ? `${fieldName}_v2.EN_GB`
      : sourceLocale === 'EN_GB'
        ? `${fieldName}_v2.EN_US`
        : null;

  const v2Facet =
    facets[v2Key] || (alternateV2Key ? facets[alternateV2Key] : null);

  if (v2Facet) {
    const labelToId = buildLabelToIdMap(v2Facet);
    const englishId = labelToId.get(value);
    if (englishId) {
      return englishId;
    }
  }

  // Fallback: Try index-based translation using regular facets
  const sourceKey = `${fieldName}.${sourceLocale}`;
  const targetKey = `${fieldName}.EN_US`;
  const alternateTargetKey = `${fieldName}.EN_GB`;

  const sourceFacet = facets[sourceKey];
  const targetFacet = facets[targetKey] || facets[alternateTargetKey];

  if (sourceFacet && targetFacet) {
    const sourceKeys = Object.keys(sourceFacet);
    const targetKeys = Object.keys(targetFacet);
    const sourceIndex = sourceKeys.indexOf(value);

    if (sourceIndex >= 0 && targetKeys[sourceIndex]) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          `[Search Translation] Falling back to index-based translation for field "${fieldName}". ` +
            `v2 facets not available (looked for ${v2Key}). ` +
            `This may cause incorrect translations if facet ordering differs between locales.`,
        );
      }
      return targetKeys[sourceIndex];
    }
  }

  // No translation available, return original value
  return value;
}

function extractQuery(uiState: UiStateAny): string | undefined {
  const s = Object.values(uiState).find((st): st is { query: string } =>
    isNonEmptyString((st as { query?: unknown })?.query),
  );
  return s?.query?.trim();
}

function inferActiveTab(uiState: UiStateAny): string | undefined {
  const order = ['watches', 'straps', 'eyewear', 'stores', 'all'];

  // First pass: find tab with actual refinements (user-applied filters) or range filters
  for (const tab of order) {
    const st = uiState[tab];
    if (!st) continue;
    const hasRefinements =
      st?.refinementList &&
      Object.keys(st.refinementList).some(
        (k) =>
          Array.isArray(st.refinementList[k]) &&
          st.refinementList[k].length > 0,
      );
    // Also check for range/price refinements (must have actual numeric values)
    const hasRangeRefinement = hasValidPriceRange(st?.range?.price);
    if (hasRefinements || hasRangeRefinement) return tab;
  }

  // Second pass: find tab with configure (but no refinements)
  for (const tab of order) {
    const st = uiState[tab];
    if (!st) continue;
    const hasConfigure =
      st?.configure &&
      (isNonEmptyString(st.configure?.filters) ||
        typeof st.configure?.page === 'number' ||
        isNonEmptyString(st.configure?.query));
    if (hasConfigure) return tab;
  }

  return order.find((t) => uiState[t]);
}

function collectRefinements(
  uiState: UiStateAny,
  serverState?: InstantSearchServerState,
  sourceLocale?: string,
): Map<string, Set<string>> {
  const refinements = new Map<string, Set<string>>();

  // Get facets from serverState for translation
  const initialResults = serverState?.initialResults || {};
  const firstResult = Object.values(initialResults)[0];
  const facets = firstResult?.results?.[0]?.facets;

  // Debug: Log available facet keys to help diagnose translation issues
  if (sourceLocale && facets) {
    const v2Keys = Object.keys(facets).filter((k) => k.includes('_v2'));
    if (v2Keys.length === 0) {
      console.warn(
        '[Search Translation] No v2 facets found in facet data. Available facet keys:',
        Object.keys(facets).slice(0, 20),
      );
    }
  }

  const add = (key: string, values: unknown) => {
    if (!Array.isArray(values)) return;
    const base = key.split('.')[0];

    // Extract locale from key if present (e.g., "type.ES" -> "ES")
    const keyLocale = key.includes('.') ? key.split('.')[1] : sourceLocale;

    const cleaned = values
      .map((v) => {
        const strValue = String(v ?? '').trim();
        if (!strValue) return '';

        // Translate to English if we have the necessary data
        if (sourceLocale && keyLocale && facets) {
          return translateToEnglish(strValue, base, keyLocale, facets);
        }
        return strValue;
      })
      .filter(Boolean);

    // Always add the key to refinements, even if it's empty
    // This ensures cleared filters are tracked
    if (!refinements.has(base)) refinements.set(base, new Set<string>());
    cleaned.forEach((v) => refinements.get(base)!.add(v));
  };

  // Prefer collecting refinements from the active tab only
  const tabKeys = new Set(['watches', 'straps', 'eyewear', 'stores', 'all']);
  const activeTab = inferActiveTab(uiState);
  const keysToProcess: string[] = [];
  if (activeTab && uiState[activeTab]) {
    keysToProcess.push(activeTab);
  } else {
    // Fallback to any known tab keys present in uiState
    Object.keys(uiState).forEach((k) => {
      if (tabKeys.has(k)) keysToProcess.push(k);
    });
  }

  if (keysToProcess.length > 0) {
    keysToProcess.forEach((key) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- uiState slice comes from Algolia InstantSearch; structure is runtime-defined
      const st: any = uiState[key];
      const rl = st?.refinementList;
      if (!rl || typeof rl !== 'object') return;
      Object.entries(rl).forEach(([k, v]) => add(k, v));
    });
  } else {
    // Final fallback: process all states (previous behaviour)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- uiState slice comes from Algolia InstantSearch; structure is runtime-defined
    Object.values(uiState).forEach((st: any) => {
      const rl = st?.refinementList;
      if (!rl || typeof rl !== 'object') return;
      Object.entries(rl).forEach(([k, v]) => add(k, v));
    });
  }

  return refinements;
}

function addPageParam(uiState: UiStateAny, params: QueryParams): void {
  const st = Object.values(uiState).find((s): s is { page: number } => {
    const page = (s as { page?: unknown })?.page;
    return typeof page === 'number' && page > 1;
  });
  if (st) params.page = st.page;
}

function addRangeParams(uiState: UiStateAny, params: QueryParams): void {
  const st = Object.values(uiState).find(
    (s): s is { range: { price: unknown } } =>
      hasValidPriceRange((s as { range?: { price?: unknown } })?.range?.price),
  );
  if (!st) return;
  const [min, max] = String(st.range.price).split(':');
  const minNum = parseFloat(min);
  const maxNum = parseFloat(max);
  // Only add params for finite values (not NaN, not Infinity)
  if (Number.isFinite(minNum)) {
    params.priceFrom = String(minNum);
  }
  if (Number.isFinite(maxNum)) {
    params.priceTo = String(maxNum);
  }
}

function addSortParam(uiState: UiStateAny, params: QueryParams): void {
  const st = Object.values(uiState).find((s): s is { sortBy: string } =>
    isNonEmptyString((s as { sortBy?: unknown })?.sortBy),
  );
  if (!st) return;
  const sortBy = String(st.sortBy);
  if (sortBy.endsWith('price_asc')) params['sort-by'] = 'price_asc';
  if (sortBy.endsWith('price_desc')) params['sort-by'] = 'price_desc';
  if (sortBy.endsWith('newest')) params['sort-by'] = 'newest';
}

/**
 * Builds URL query params from UI state.
 * Optionally translates locale-specific facet values to English for URL consistency.
 *
 * @param uiState - The current UI state from InstantSearch
 * @param serverState - Optional server state containing facet data for translation
 * @param sourceLocale - Optional source locale code (e.g., 'ES') for translation
 */
export function buildSearchPageQueryParamsFromUiState(
  uiState: UiStateAny,
  serverState?: InstantSearchServerState,
  sourceLocale?: string,
): QueryParams {
  const params: QueryParams = {};

  const q = extractQuery(uiState);
  if (q) params.q = q;

  const activeTab = inferActiveTab(uiState);
  if (activeTab) params.urlParts = [activeTab];

  const refinements = collectRefinements(uiState, serverState, sourceLocale);
  Array.from(refinements.entries()).forEach(([k, set]) => {
    // Only add to params if the set has values
    // This ensures empty/cleared filters are not included in the URL
    if (set.size > 0) {
      params[k] = Array.from(set);
    }
  });

  addPageParam(uiState, params);
  addRangeParams(uiState, params);
  addSortParam(uiState, params);

  return params;
}
